/**
 * Visual overlay over IA elements in editor. Can be controlled by moving & resizing
 */
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import styled from 'styled-components'
import interact from 'interact.js'
import { Button, Tooltip } from 'wix-style-react'
import {
  Undo,
  Check,
  Visibility,
  VisibilityHidden} from 'wix-style-react/dist/src/Icons'
import _ from 'lodash'

import { getCustomStylesById } from 'utils/appUtils'
import {
  updateCustomStyleEffect,
  setCustomFieldArray,
  updateCustomStyleEffects,
  updateCustomStyleEffectLocally,
  getCustomStyleClassName
} from 'controllers/logic'
import { POSITION, ZOOM, HIDDEN, POSITION_ZOOM_ALL } from 'constants/stylePresetType'
import FieldButton from './FieldButton'
import * as customFieldTypes from 'constants/customFieldTypes'
import * as fieldMenuButtonTypes from 'constants/fieldMenuButtonTypes'
import SPPositionZoomAll from './popovers/customCSS/stylePresetComponents/SPPositionZoomAll'
import getFieldButtonTypes from './fieldMenu/fieldButtonTypes'
import Text from 'components/common/Text'
import ResizeAnchors from 'components/shared/ResizeAnchors'

// === Styled components ===
const Container = styled.div`
  position: fixed;
`
export const StyledOverlay = styled.div`
  position: fixed;
  border-style: ${props => props.editMode ? 'dashed' : 'solid'};
  border-width: 2px;
  pointer-events: ${props => props.pointerEvents};
  z-index: 15;
  ${props => props.overlayStyle}
`
const ButtonsContainer = styled.div`
  display: flex;
  height: 40px;
  align-items: flex-start;
  justify-content: space-between;
  .buttons-group {
    & > * {
      margin-right: 5px;
      z-index: 16;
    }
  }
`
const MenuContainer = styled.div`
  position: fixed;
  display: flex;
  align-items: flex-start;
  justify-content: space-around;

  &:after {
    content: '';
    display: 'block';
    position: absolute;
    top: 40px;
    right: 0;
    height: ${props => props.height};
    width: calc( 100% - ${props => props.width});
  }
`

const InputContainer = styled.div`
  position: absolute;
  top: -2px;
  left: -2px;
  width: calc(100% + 4px);
  display: flex;
  border: 2px solid rgba(101, 182, 100, 0.64);
  flex-direction: column;
  min-height: 30px;
  height: 100%;
  background: rgba(0,0,0,.3);

  input {
    min-width: 11em;
    height: 100%;
    width: 100%;
    font-size: 12px;
    outline: none;
    margin-bottom: 3px;
    pointer-events: auto !important;
    border: none;
    box-shadow: 0px 0px 5px 0px black;
    padding: 2px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`

class Overlay extends Component {
  constructor (props) {
    super(props)
    this.state = {
      moveStylePreset: null,
      zoomStylePreset: null,
      pointerEvents: false,
      moving: false,
      showTooltip: true,
      resizing: false,
      textEdit: false,
      fieldText: '',
      editingBullets: false,
      bulletsFieldText: [],
      isEditingUrl: false,
      rect: null
    }

    this.hiddenPreset = null
    this.hiddenSwitchValue = false
    this.handlePageClick = this.handlePageClick.bind(this)
    this.timerId = setInterval(this.updateRect, 30)

  }

  componentDidMount () {
    document.getElementsByTagName('body')[0].addEventListener('click', this.handlePageClick)
    this.htmlElement = document.querySelector('html')
    this.setNewStylePresets(this.props)
    const { elt } = this.calcRect()
    this.processHiddenElement(elt)
  }

  processHiddenElement = (elt) => {
    const { field, app } = this.props
    if (elt && field && field.stylePresets) {
      this.hiddenPreset = field.stylePresets.find(preset => (preset.presetType === HIDDEN))
      if (this.hiddenPreset) {
        const className = this.getClassName(this.hiddenPreset.className)
        const customStyles = getCustomStylesById(app, className)
        if (customStyles[HIDDEN]) {
          this.hiddenSwitchValue = customStyles[HIDDEN].value
          if (this.hiddenSwitchValue === true) {
            elt.style.display = window.getComputedStyle(elt).getPropertyValue('display')
            elt.style.opacity = '0.3'
          }
        }
      }
    }
  }

  calcRect = () => {
    const { field } = this.props
    let newRect
    let elt
    if ('overlayKey' in field) {
      const key = field.overlayKey
      elt = document.getElementById(key)
      if (elt && 'getBoundingClientRect' in elt) {
        newRect = elt.getBoundingClientRect()
      }
    }
    return { newRect, elt }
  }

  updateRect = () => {
    const { newRect, elt } = this.calcRect()
    const { rect } = this.state
    if (!rect) {
      // console.log('!rect, setState')
      this.setState({ rect: newRect })
      return
    }
    if (newRect) {
      if (rect.width !== newRect.width || rect.height !== newRect.height || rect.top !== newRect.top || rect.left !== newRect.left) {
        // console.log('newRect !== old rect, update')
        this.processHiddenElement(elt)
        this.setState({ rect: newRect })
      }
    }
  }

  componentWillUnmount () {
    document.getElementsByTagName('body')[0].removeEventListener('click', this.handlePageClick)
    clearInterval(this.timerId)
    this.updateRect = () => null
  }

  componentWillReceiveProps (nextProps) {
    const { field, app } = nextProps

    if (nextProps.field !== this.props.field) { // If we switching between elements, setting new style presets
      this.setNewStylePresets(nextProps)
      this.setState({
        editingBullets: false,
        fieldText: '',
        bulletsFieldText: []
      })
    }
    if (field && field.stylePresets) {
      this.hiddenPreset = field.stylePresets.find(preset => (preset.presetType === HIDDEN))

      if (this.hiddenPreset) {
        const customStyles = getCustomStylesById(app, this.getClassName(this.hiddenPreset.className))
        if (customStyles[HIDDEN]) {
          this.hiddenSwitchValue = customStyles[HIDDEN].value
        }
      }
    }

    if (nextProps.moving !== this.props.moving) {
      if (nextProps.moving) {
        this.setState({moving: true}, () => {
          this.enablePointerEvents()
        })
      } else {
        this.setState({moving: false}, () => {
          this.disablePointerEvents()
        })
      }
    }
  }

  handlePageClick (e) {
    if (!this.state) return
    if (this.state.resizing) {
      return this.setState({resizing: false})
    }
    if (this.props.isEditingText) {
      let isInput = false
      for (const target of e.path) {
        if (target.className && 'TextEditInput'.indexOf(target.className) > -1) {
          isInput = true
        }
      }
      if (!isInput) {
        this.finishEditingText()
        this.props.setIsEditingText(false)
      }
      return
    }
    if (typeof e.target.className !== 'string' && e.target.tagName !== 'svg' && e.target.tagName !== 'path') {
      return this.props.enableHover()
    }
    if (typeof e.target.className === 'string' && !e.target.className.includes('Overlay') && !e.target.className.includes('js-field-button') && this.props.hoverDisabled && !this.state.resizing) {
      this.props.enableHover()
    }
  }

  setNewStylePresets = (props) => {
    if (!props || !props.field) return
    const { field: { stylePresets } } = props

    if (stylePresets) {
      this.setState({
        moveStylePreset: stylePresets.find(preset => (preset.presetType === POSITION)),
        zoomStylePreset: stylePresets.find(preset => (preset.presetType === ZOOM)),
      }, () => this.initMoveResizeControls(this.overlayRef))
    }
  }

  getClassName = (className) => {
    const { app, field } = this.props
    return getCustomStyleClassName(app, field, className)
  }

  onDragStart = () => {
    const { app } = this.props
    if (this.props.isEditingText) return
    this.initialX = 0
    this.initialY = 0
    this.currentX = 0
    this.currentY = 0
    this.rem = parseFloat(this.htmlElement.style.fontSize)

    const customStyles = getCustomStylesById(app, this.getClassName(this.state.moveStylePreset.className))
    if (customStyles[POSITION]) {
      this.initialX = customStyles[POSITION].x
      this.initialY = customStyles[POSITION].y
    }

    this.props.disableHover()
    // console.log('onDragStart', this.initialX, this.initialY)
  }

  updatePositionLocallySimple = () => {
    const { dispatch } = this.props
  //  console.log('updateLocally')
    dispatch(updateCustomStyleEffectLocally(
      this.props.app,
      this.getClassName(this.state.moveStylePreset.className),
      POSITION,
      { x: this.initialX + this.currentX, y: this.initialY + this.currentY }
    ))
  }

  updatePositionLocally = _.throttle(this.updatePositionLocallySimple, 30)

  onDragMove = e => {
    // console.log('onDragMove', e)
    if (this.props.isEditingText) return
    this.currentX += e.dx / this.rem
    this.currentY += e.dy / this.rem
    this.updatePositionLocallySimple()

//    e.target.style.marginLeft = this.currentX + 'rem'
//    e.target.style.marginTop = this.currentY + 'rem'
  }

  onDragEnd = e => {
    if (this.props.isEditingText) return
    const { dispatch, setIgnorePopoverOusideClick } = this.props
    dispatch(updateCustomStyleEffect(
      this.props.app,
      this.getClassName(this.state.moveStylePreset.className),
      POSITION,
      { x: this.initialX + this.currentX, y: this.initialY + this.currentY }
    ))

    e.target.style.marginLeft = '0'
    e.target.style.marginTop = '0'
    setIgnorePopoverOusideClick(true)
    this.props.enableHover()
    // console.log('onDragEnd', this.props.field, this.currentX, this.currentY)
  }

  switchHidden = (value) => {
    const { dispatch, app } = this.props
    if (this.hiddenPreset) {
      dispatch(updateCustomStyleEffect(
        app,
        this.getClassName(this.hiddenPreset.className),
        HIDDEN,
        { value }
      ))
    }
  }

  onResizeStart = e => {
    if (this.props.isEditingText) return
    this.initialZoom = 1
    this.initialWidth = e.rect.width

    this.setState({resizing: true})

    const customStyles = getCustomStylesById(this.props.app, this.getClassName(this.state.zoomStylePreset.className))
    if (customStyles[ZOOM]) {
      this.initialZoom = customStyles[ZOOM].zoom
    }
    this.props.disableHover()
    // console.log('onResizeStart', this.initialWidth)
  }

  onResizeProgress = e => {
    if (this.props.isEditingText) return
    this.currentWidth = e.rect.width
    const { dispatch } = this.props
  //  console.log('updateLocally')
    dispatch(updateCustomStyleEffectLocally(
      this.props.app,
      this.getClassName(this.state.zoomStylePreset.className),
      ZOOM,
      { zoom: this.initialZoom * this.currentWidth / this.initialWidth }
    ))
    // e.target.style.transform = `scale(${this.currentWidth / this.initialWidth})`
  }

  onResizeEnd = e => {
    if (this.props.isEditingText) return
    const { dispatch, setIgnorePopoverOusideClick } = this.props
    dispatch(updateCustomStyleEffect(
      this.props.app,
      this.getClassName(this.state.zoomStylePreset.className),
      ZOOM,
      { zoom: this.initialZoom * this.currentWidth / this.initialWidth }
    ))

    e.target.style.transform = ''
    setIgnorePopoverOusideClick(true)
    this.props.enableHover()
    // console.log('onResizeEnd', this.currentWidth)
  }

  initMoveResizeControls (elementRef) {
    // console.log('initMoveResizeControls', elementRef)
    if (elementRef) {
      let draggable
      if (this.state.moveStylePreset) { // Dragging is allowed for this element
        draggable = {
          onstart: this.onDragStart,
          onmove: this.onDragMove,
          onend: this.onDragEnd,
        }
      } else { // Dragging is not allowed for this element
        draggable = { enabled: false }
      }

      let resizable
      if (this.state.zoomStylePreset) { // Resizing is allowed for this element
        resizable = {
          preserveAspectRatio: true,
          margin: 7, // distance form bound to define resizing-action area of mouse cursor
          edges: { left: true, right: true, bottom: true, top: true },
          onstart: this.onResizeStart,
          onmove: this.onResizeProgress,
          onend: this.onResizeEnd,
        }
      } else { // Resizing is not allowed for this element
        resizable = { enabled: false }
      }
      // console.log('set interact for the element', elementRef)
      interact(elementRef).draggable(draggable).resizable(resizable)
    }
  }

  enablePointerEvents = () => {
    this.setState({pointerEvents: true})
  }

  disablePointerEvents = () => {
    this.setState({pointerEvents: false})
  }

  enableMoving = () => {
    this.setState({moving: true}, () => {
      this.props.setIgnorePopoverOusideClick(true)
      this.props.disableHover()
      this.enablePointerEvents()
    })
  }

  disableMoving = () => {
    this.setState({moving: false}, () => {
      this.props.setIgnorePopoverOusideClick(false)
      this.props.enableHover()
      this.disablePointerEvents()
    })
  }

  updateEffect = (className, effectName) => {
    return (value) => {
      const { dispatch, app } = this.props
      dispatch(updateCustomStyleEffect(app, className, effectName, value))
    }
  }

  renderMovingMenu (positionPreset, zoomPreset) {
    return (
      <ButtonsContainer>
        <div className='buttons-group'>
          <FieldButton
            className='js-field-button'
            label='Done'
            icon={<Check size='1em' />}
            clickHandler={this.disableMoving} />
          {positionPreset &&
            <FieldButton
              className='js-field-button'
              clickHandler={this.updateEffect(this.getClassName(positionPreset.className), positionPreset.presetType)}
              icon={<Undo size='1em' />}
              label='Reset position'
            />
          }
          {zoomPreset &&
            <FieldButton
              className='js-field-button'
              clickHandler={this.updateEffect(this.getClassName(zoomPreset.className), zoomPreset.presetType)}
              icon={<Undo size='1em' />}
              label='Reset size'
            />
          }
        </div>
      </ButtonsContainer>
    )
  }

  renderTextEditMenu () {
    return (
      <div className='js-field-button'>
        <Button
          theme='whiteblueprimary'
          prefixIcon={<Check size='5em' />}
          onClick={() => this.finishEditingText()}
        >Done</Button>
      </div>
    )
  }

  setMenuHeight (parentWidth, parentHeight) {
    const element = document.getElementById('MenuContainer')
    let elWidth = 0
    if (element && 'getBoundingClientRect' in element) {
      elWidth = element.getBoundingClientRect().width
    }
    if (elWidth > parentWidth) {
      if (parentHeight > 300) {
        return 'auto'
      }
      return parentHeight + 40 + 'px'
    } else {
      return 'auto'
    }
  }

  setMenuAlignment (parentLeft) {
    const element = document.getElementById('MenuContainer')
    let elWidth = 0
    if (element && 'getBoundingClientRect' in element) {
      elWidth = element.getBoundingClientRect().width
    }
    if ((parentLeft + elWidth) > window.innerWidth) {
      return '60px'
    }
    return 'auto'
  }


  visibilityButtonClick = () => {
    this.switchHidden(!this.hiddenSwitchValue)
    const className = this.getClassName(this.hiddenPreset.className)
    const elt = document.getElementsByClassName(className)[0]
    if (this.hiddenSwitchValue) {
      elt.style.display = window.getComputedStyle(elt).getPropertyValue('display')
      elt.style.opacity = '1'
    } else {
      elt.style.display = window.getComputedStyle(elt).getPropertyValue('display')
      elt.style.opacity = '0.3'
    }
  }

  renderVisibilityButton () {
    return (
      <FieldButton
        label={(this.hiddenSwitchValue) ? 'Show this element' : 'Hide this element'}
        icon={(this.hiddenSwitchValue) ? <Visibility size='1em' /> : <VisibilityHidden size='1em' />}
        clickHandler={this.visibilityButtonClick}
      />
    )
  }

  finishEditingText () {
    const { dispatch, field, app } = this.props
    if (this.state.editingBullets) {
      this.state.bulletsFieldText.map((text, index) => {
        const path = field.paths[index]
        if (path) {
          dispatch(setCustomFieldArray(app.id, path, text))
        }
      })
    } else {
      if (this.state.isEditingUrl) {
        const path = field.related['url']
        if (path) {
          dispatch(setCustomFieldArray(app.id, path, this.state.fieldText))
        }
        this.setState({
          isEditingUrl: false
        })
      } else {
        const path = field.paths[0]
        if (path) {
          dispatch(setCustomFieldArray(app.id, path, this.state.fieldText))
        }
      }

    }
  }

  getCurrentValue = (className, effectName) => {
    const customFields = this.props.app.customFields
    const currentValue = _.get(customFields, ['customStyles', className, effectName], {})
    return { ...currentValue }
  }

  updatePositionZoomEffects = (classNames, getSlideClassNameFn) => {
    return () => {
      // console.log('updatePositionZoomEffects', classNames)
      const { dispatch, app } = this.props
      const slidesNumber = app.slidesCnt || 3 // TODO Hardcoded because no information in app, no slidesCnt when Shopify is enabled
      const positionValue = this.getCurrentValue(this.getClassName(classNames[0]), POSITION)
      const zoomValue = this.getCurrentValue(this.getClassName(classNames[0]), ZOOM)

      classNames.forEach(classNameRaw => {
        const className = this.getClassName(classNameRaw)
        // console.log('apply to', classNameRaw, ':', className)
        dispatch(updateCustomStyleEffects(app, className, {
          [POSITION]: positionValue,
          [ZOOM]: zoomValue,
        }))
      })

      for (let i = 0; i < slidesNumber; i++) {
        dispatch(updateCustomStyleEffects(app, getSlideClassNameFn(i, app.customFields), {
          [POSITION]: positionValue,
          [ZOOM]: zoomValue,
        }))
      }
    }
  }

  setFieldButtonHandler (field, buttonType, isBullets) {
    const { app, settings } = this.props
    switch (buttonType) {
      case fieldMenuButtonTypes.OPEN.type:
        return () => {
          const targetEl = field.overlayKey
          document.getElementById(targetEl).click()
        }
      case fieldMenuButtonTypes.DESIGN.type:
      case fieldMenuButtonTypes.PRODUCTS.type:
      case fieldMenuButtonTypes.EDIT_LAYOUT.type:
      case fieldMenuButtonTypes.CHOOSE_TYPE.type:
      case fieldMenuButtonTypes.ASSETS.type:
        return () => {
          this.props.setPopoverSection(buttonType)
          this.props.openEditMenu(this.props.fieldNum)
        }
      case fieldMenuButtonTypes.EDIT_URL.type:
        if (!field.related || !field.related['url']) return () => { console.log('no function binded to that button') }
        return () => {
          const value = _.get(app.customFields, field.related['url']) || _.get(settings, field.related['url'])
          this.props.setIsEditingText(true)
          this.setState({
            isEditingUrl: true,
            fieldText: value
          })
        }
      case fieldMenuButtonTypes.EDIT_EMAIL.type:
        return () => {
          this.props.openEditMenu(field.related.emailFieldNum)
        }
      case fieldMenuButtonTypes.EDIT_TEXT.type:
      case fieldMenuButtonTypes.EDIT_SOCIAL.type:
        if (isBullets) {
          return () => {
            const values = field.paths.map(path =>
              _.get(app.customFields, path) || _.get(settings, path)
            )
            this.props.setIsEditingText(true)
            this.setState({
              editingBullets: true,
              bulletsFieldText: values
            })
          }
        } else {
          return () => {
            const value = _.get(app.customFields, field.paths[0]) || _.get(settings, field.paths[0])
            this.props.setIsEditingText(true)
            this.setState({
              fieldText: value
            })
          }
        }
      default:
        return () => { console.log('no function binded to that button') }
    }
  }


  renderMenuButtons = () => {
    const { field } = this.props
    let POSITION_ZOOM_ALL_PRESET
    if (field.stylePresets) {
      POSITION_ZOOM_ALL_PRESET = field.stylePresets.find(preset => (preset.presetType === POSITION_ZOOM_ALL))
    }
    return (
      <div className='buttons-group'>
        {getFieldButtonTypes(field.type).map((button, index) => {
          if (!_.hasIn(field, 'stylePresets') && button.type === fieldMenuButtonTypes.DESIGN.type && field.type !== customFieldTypes.SHOPIFY_VARIANTS && field.type !== customFieldTypes.BUTTONS_CONTAINER) {
            return null
          } else {
            return (
              <FieldButton
                isActive={this.props.activeButton === button.type}
                key={index}
                className='js-field-button'
                icon={button.icon({size: '1em'})}
                clickHandler={this.setFieldButtonHandler(field, button.type, button.isBullets)}
                label={button.label} />
            )
          }
        })}
        { POSITION_ZOOM_ALL_PRESET
          ? <SPPositionZoomAll isFieldButton onChange={this.updatePositionZoomEffects(POSITION_ZOOM_ALL_PRESET.classNames, POSITION_ZOOM_ALL_PRESET.getSlideClassName, POSITION_ZOOM_ALL_PRESET.presetType)} />
          : null
        }
      </div>
    )
  }

  renderMenu (field, top, left, parentWidth, parentHeight) {
    let POSITION_PRESET, ZOOM_PRESET, HIDDEN_PRESET
    if (field.stylePresets) {
      POSITION_PRESET = field.stylePresets.find(preset => (preset.presetType === POSITION))
      ZOOM_PRESET = field.stylePresets.find(preset => (preset.presetType === ZOOM))
      HIDDEN_PRESET = field.stylePresets.find(preset => (preset.presetType === HIDDEN))
    }
    return (
      <MenuContainer
        style={{top: top - 40, left: left, right: this.setMenuAlignment(left)}}
        id={'MenuContainer'}
        height={`${parentHeight}px`}
        width={`${parentWidth}px`}
      >
        {this.state.moving && this.renderMovingMenu(POSITION_PRESET, ZOOM_PRESET)}
        {this.props.isEditingText && this.renderTextEditMenu()}
        {!this.state.moving && !this.props.isEditingText && (
          <ButtonsContainer id='ButtonsContainer'>
            {this.renderMenuButtons()}
            { HIDDEN_PRESET && this.renderVisibilityButton()}
          </ButtonsContainer>
        )}
      </MenuContainer>
    )
  }

  renderTextEditor () {
    if (this.state.editingBullets) {
      return <InputContainer>
        {this.state.bulletsFieldText.map((field, index) => {
          return (
            <input key={index} type='text'
              className='TextEditInput'
              value={field}
              onChange={e => {
                const value = e.target.value
                this.setState(prevState => {
                  const bullets = prevState.bulletsFieldText
                  bullets[index] = value
                  return {
                    bulletsFieldText: bullets
                  }
                })
              }}
              onKeyDown={e => {
                if (e.keyCode === 13) {
                  this.finishEditingText()
                  this.props.setIsEditingText(false)
                }
              }}
            />
          )
        })}
      </InputContainer>
    }
    return (
      <InputContainer>
        <input type='text'
          className='TextEditInput'
          value={this.state.fieldText}
          onChange={e => this.setState({fieldText: e.target.value})}
          onKeyDown={e => {
            if (e.keyCode === 13) {
              this.finishEditingText()
              this.props.setIsEditingText(false)
            }
          }}
        />
      </InputContainer>
    )
  }

  onStyledOverlayMounted = ref => {
    // console.log('onStyledOverlayMounted')
    this.overlayRef = ref
    if (ref) this.initMoveResizeControls(this.overlayRef)
  }

  render () {
    const { field } = this.props
    const { rect } = this.state
    if (!field) return null
    const overlayStyle = field.overlayStyle
    if (overlayStyle && rect) {
      let POSITION_PRESET = null
      if (field.stylePresets) {
        POSITION_PRESET = field.stylePresets.find(preset => (preset.presetType === POSITION))
      }
      const style = {
        left: `${rect.left}px`,
        top: `${rect.top}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`
      }
      const shouldTooltipBeShown = (this.state.showTooltip && !this.props.isEditorPopoverVisible && !this.props.isEditingText && _.hasIn(field, 'stylePresets') && POSITION_PRESET !== undefined)
      return (
        // <Container >
        <Tooltip
          shouldCloseOnClickOutside
          active={shouldTooltipBeShown}
          // hideTrigger='custom'
          showTrigger='custom'
          //moveBy={{x: 0, y: 7}}
          placement='bottom'
          alignment='center'
          hideDelay={0}
          showDelay={1000}
          content={<Text>Click to move or resize</Text>}
        >
          <Container
            id='overlay'
            style={style}
            // left={`${rect.left}px`}
            // top={`${rect.top}px`}
            // width={`${rect.width}px`}
            // height={`${rect.height}px`}
            // onClick={this.props.onClick}
          >

            <StyledOverlay
              style={style}
              // left={`${rect.left}px`}
              // top={`${rect.top}px`}
              // width={`${rect.width}px`}
              // height={`${rect.height}px`}
              overlayStyle={overlayStyle}
              editMode={this.state.moving}
              // pointerEvents={`${this.state.pointerEvents ? 'auto' : 'none'}`}
              innerRef={this.onStyledOverlayMounted}
              onClick={this.props.onClick}
            >
              {this.state.zoomStylePreset && <ResizeAnchors visible={this.state.moving} />}
              {this.props.isEditingText && this.renderTextEditor()}
            </StyledOverlay>
            <div id='MenuContainer' onClick={() => null}>
              {this.renderMenu(field, rect.top, rect.left, rect.width, rect.height)}
            </div>
          </Container>

        </Tooltip>
        // </Container>
      )
    } else {
      return null
    }
  }
}

Overlay.propTypes = {
  field: PropTypes.object,
  fieldNum: PropTypes.number,
  openEditMenu: PropTypes.func,
  app: PropTypes.object.isRequired,
  setIgnorePopoverOusideClick: PropTypes.func.isRequired,
  disableHover: PropTypes.func.isRequired,
  enableHover: PropTypes.func.isRequired,
  setPopoverSection: PropTypes.func,
  moving: PropTypes.bool,
  hoverDisabled: PropTypes.bool,
  isEditorPopoverVisible: PropTypes.bool.isRequired,
  settings: PropTypes.object.isRequired,
  setIsEditingText: PropTypes.func.isRequired,
  isEditingText: PropTypes.bool.isRequired,
  activeButton: PropTypes.string,
  onClick: PropTypes.func
}

ResizeAnchors.propTypes = {
  visible: PropTypes.bool
}

export default connect()(Overlay)



// WEBPACK FOOTER //
// ./src/components/editor/Overlay.js